import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen';

import { fundFilters } from './fund.filters';
import { FUND_TABS } from './fund.search';

// Manifestos de Cessionários (U23, U42). Este arquivo entra no JS inicial (src/app/screens.ts): o fio
// das ações com efeito vem por `import()` na hora de rodar, e a tela usa o mesmo manifesto.

export const FUND_LIST_PATH = '/cadastro/fundos';
export const fundPath = (id: string) => `${FUND_LIST_PATH}/${encodeURIComponent(id)}`;

const idInput = z.object({ id: z.string().min(1).max(64) });
const nameInput = idInput.extend({ name: z.string().max(200).optional() });

const loadApi = () => import('./fund.api');

export const fundCreate = action({
  id: 'fund.create',
  kind: 'draft',
  label: msg`Novo cadastro`,
  description: msg`Abre o formulário em branco para cadastrar um cessionário (fundo).`,
  keywords: [msg`cessionário`, msg`fundo`, msg`cadastrar`],
  shortcut: 'n',
  permission: can('Fund', 'Create'),
  run: ({ nav }) => nav.to(fundPath('novo')),
});

export const fundFilter = filterAction('fund.filter', fundFilters, {
  description: msg`Filtra a lista de cessionários: searchString (nome, CNPJ ou e-mail), name, registrationNumber (CPF ou CNPJ), email e initialDate/finalDate (data de criação, AAAA-MM-DD). Filtro omitido é limpo.`,
});

export const fundListPage = listPageAction('fund.list.page');

/** Página, tamanho e ordem da aba Cessões (a mesma URL do cadastro). */
export const fundAssignmentsPage = listPageAction('fund.assignments.page');

export const fundOpen = action({
  id: 'fund.open',
  kind: 'navigate',
  page: true,
  row: true,
  label: msg`Abrir cessionário`,
  description: msg`Abre o cadastro de um cessionário pelo id, com o formulário e as abas.`,
  input: idInput,
  run: ({ nav, input }) => nav.to(fundPath(input.id)),
});

export const fundDelete = action({
  id: 'fund.delete',
  kind: 'destructive',
  page: true,
  row: true,
  label: msg`Excluir`,
  description: msg`Exclui um cessionário pelo id. Não dá para desfazer. Pede a confirmação da pessoa.`,
  permission: can('Fund', 'Delete'),
  input: nameInput,
  // U20-6: o verbo e o nome; o texto é o do legado com "cessionário" (01 §5).
  preview: (input) => {
    const name = input.name ?? t`este cessionário`;
    return { title: t`Excluir ${name}?`, detail: t`Tem certeza que deseja excluir este cessionário?`, tone: 'destructive' };
  },
  run: async ({ input }) => {
    const api = await loadApi();
    await api.deleteFund(input.id);
    return input.id;
  },
});

export const fundBack = action({
  id: 'fund.back',
  kind: 'navigate',
  label: msg`Voltar para Cessionários`,
  description: msg`Sai do cadastro do cessionário e mostra a lista de cessionários de novo.`,
  run: ({ nav }) => nav.to(FUND_LIST_PATH),
});

export const fundTab = action({
  id: 'fund.tab',
  kind: 'navigate',
  label: msg`Trocar de aba`,
  description: msg`Troca a aba do cessionário aberto: informacoes, envolvidos (signatários), documentos ou cessoes (cessões, só leitura).`,
  input: z.object({ tab: z.enum(FUND_TABS) }),
  run: ({ nav, input }) => nav.search({ tab: input.tab === 'informacoes' ? undefined : input.tab, page: undefined, size: undefined, orderBy: undefined }),
});

export const fundClone = action({
  id: 'fund.clone',
  kind: 'write',
  label: msg`Clonar registro`,
  description: msg`Copia o cessionário para o correspondente escolhido (tenant) e abre a cópia. Pede a confirmação da pessoa.`,
  permission: can('Fund', 'Update'),
  input: nameInput.extend({ tenant: z.string().min(1).max(64) }),
  preview: (input) => {
    const name = input.name ?? t`o cessionário`;
    const tenant = input.tenant;
    return { title: t`Clonar ${name}`, detail: t`Cria uma cópia no correspondente ${tenant}. Você é levado para a cópia em seguida.` };
  },
  run: async ({ input, nav }) => {
    const api = await loadApi();
    const created = await api.cloneFund(await api.getFund(input.id), input.tenant);
    const id = Array.isArray(created) ? created[0] : created;
    if (id) await nav.to(fundPath(id));
    return id;
  },
});

export const fundAssign = action({
  id: 'fund.assign',
  kind: 'write',
  label: msg`Atribuir`,
  description: msg`Atribui o cadastro do cessionário a um operador (userId) ou a um grupo (groupName) de um correspondente (tenant). Pede a confirmação da pessoa.`,
  permission: can('Fund', 'Update'),
  input: idInput
    .extend({ tenant: z.string().max(64).optional(), userId: z.string().max(64).optional(), groupName: z.string().max(120).optional() })
    .refine((value) => Boolean(value.userId ?? value.groupName), { message: 'userId or groupName is required' }),
  preview: (input) => {
    const who = input.userId ?? input.groupName ?? '';
    return { title: t`Atribuir cessionário`, detail: input.userId ? t`O registro passa para o operador ${who}.` : t`O registro passa para o grupo ${who}.` };
  },
  run: async ({ input }) => {
    const api = await loadApi();
    await api.assignFund(input.id, { userId: input.userId, groupName: input.groupName, tenant: input.tenant });
  },
});

/**
 * Cessão do cessionário: o detalhe da cessão (`batch-assignment`), na mesma aba (U15). Caminho literal: o
 * `batch-assignment` importa a porta deste módulo, e o inverso fecharia ciclo.
 */
export const fundOpenAssignment = action({
  id: 'fund.openAssignment',
  kind: 'navigate',
  page: true,
  row: true,
  label: msg`Abrir cessão`,
  description: msg`Abre, na mesma aba, o detalhe de uma cessão deste cessionário.`,
  input: idInput,
  run: ({ input, nav }) => nav.to(`/ccb/cessoes/${encodeURIComponent(input.id)}`),
});

export const fundListScreen = defineScreen({
  id: 'fund.list',
  path: FUND_LIST_PATH,
  title: msg`Cessionários`,
  description: msg`Lista de cessionários (fundos, em geral FIDC): busca, filtros por nome, CPF/CNPJ, e-mail e data de criação, paginação, abrir e excluir.`,
  permission: can('Fund', 'Read'),
  place: { icon: 'landmark', section: 'register' },
  actions: [fundCreate, fundFilter, fundListPage, fundOpen, fundDelete],
});

export const fundDetailScreen = defineScreen({
  id: 'fund.detail',
  path: `${FUND_LIST_PATH}/$fundId`,
  title: msg`Cessionário`,
  description: msg`Cadastro de um cessionário (novo ou edição): abas de informações, envolvidos, documentos e cessões; clonar, atribuir, histórico e excluir.`,
  permission: can('Fund', 'Read'),
  place: {
    icon: 'file-text',
    parent: 'fund.list',
    record: msg`Cessionário`,
    tabs: [
      { id: 'informacoes', label: msg`Informações` },
      { id: 'envolvidos', label: msg`Envolvidos` },
      { id: 'documentos', label: msg`Documentos` },
      { id: 'cessoes', label: msg`Cessões` },
    ],
  },
  actions: [fundBack, fundTab, fundClone, fundAssign, fundDelete, fundAssignmentsPage, fundOpenAssignment],
});
